import React, { useState, useEffect } from "react";
import {
  FileText,
  Upload,
  Trash2,
  MessageSquare,
  History,
  ChevronRight,
  X,
  Shield,
  FileUp,
  Calendar,
  Bot,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import MainHeader from "../MainHeader";
import Ash from "../assets/ash.png";
import { useAuth } from "../hooks/useAuth";

const DeepResearch = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState("main");
  const [documents, setDocuments] = useState(0);
  const [chatHistory, setChatHistory] = useState(0);
  useEffect(() => {
    const fetchCounts = async () => {
      const response = await api.get("/deep_research/dashboard");

      setChatHistory(response.data?.history || 0);
      setDocuments(response.data?.file_system || 0);
    };
    fetchCounts();
  }, []);
  return (
    <>
      <MainHeader />
      <div className="min-h-screen bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-800 relative overflow-hidden">
        {/* 배경 블러 + 그리드 패턴 */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-96 h-96 bg-blue-400/30 rounded-full filter blur-[100px] animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-[500px] h-[500px] bg-purple-400/30 rounded-full filter blur-[120px] animate-pulse delay-700"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-indigo-400/20 rounded-full filter blur-[100px] animate-pulse delay-1000"></div>
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http://www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%239C92AC%22%20fill-opacity%3D%220.05%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20"></div>
        </div>

        <div className="relative z-10">
          {/* 헤더 */}
          <div className="px-6 py-12 text-center">
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="w-24 h-24 bg-white/20 backdrop-blur-lg rounded-3xl flex items-center justify-center shadow-2xl">
                  <img
                    src={Ash}
                    alt="ash logo"
                    className="w-30 h-30 text-white"
                  />
                </div>
                <div className="absolute -inset-1 bg-gradient-to-r from-blue-400 to-purple-400 rounded-3xl blur opacity-50 animate-pulse"></div>
              </div>
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-4">
              Document Deep Research
            </h1>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto">
              Ash와 함께 여러 문서를 요약하고 문서 기반의 신뢰성있는 분석을
              경험하세요!
            </p>
            <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur rounded-full">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-sm text-white/80">Ash-Preview001</span>
            </div>
          </div>

          {/* 기능 카드 */}
          <div className="max-w-7xl mx-auto px-6 pb-12 grid md:grid-cols-3 gap-6">
            {/* 파일 관리 */}
            <div
              onClick={() => navigate("/deep_research/filesystem")}
              className="group relative bg-white/10 backdrop-blur-lg rounded-3xl p-8 cursor-pointer transform transition-all hover:scale-105 hover:bg-white/20 border border-white/20"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-blue-400/20 to-transparent rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div className="relative z-10">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <FileUp className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-semibold text-white mb-3">
                  파일 관리하기
                </h3>
                <p className="text-blue-100 mb-4">
                  PDF 문서를 업로드하고 관리합니다
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-sm bg-white/20 text-white px-3 py-1 rounded-full">
                    {documents}개 문서
                  </span>
                  <ChevronRight className="w-5 h-5 text-white/60 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </div>
            {/* 질의응답 */}
            <div
              onClick={() => navigate("/deep_research/prebuild")}
              className="group relative bg-white/10 backdrop-blur-lg rounded-3xl p-8 cursor-pointer transform transition-all hover:scale-105 hover:bg-white/20 border border-white/20"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-purple-400/20 to-transparent rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div className="relative z-10">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <MessageSquare className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-semibold text-white mb-3">
                  문서 질의응답하기
                </h3>
                <p className="text-blue-100 mb-4">
                  AI와 함께 문서를 분석하고 질문합니다
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-sm bg-white/20 text-white px-3 py-1 rounded-full">
                    새로운 분석
                  </span>
                  <ChevronRight className="w-5 h-5 text-white/60 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </div>
            {/* 히스토리 */}
            <div
              onClick={() => navigate("/deep_research/history")}
              className="group relative bg-white/10 backdrop-blur-lg rounded-3xl p-8 cursor-pointer transform transition-all hover:scale-105 hover:bg-white/20 border border-white/20"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-400/20 to-transparent rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div className="relative z-10">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <History className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-semibold text-white mb-3">
                  히스토리 보기
                </h3>
                <p className="text-blue-100 mb-4">
                  이전 분석 내용을 확인합니다
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-sm bg-white/20 text-white px-3 py-1 rounded-full">
                    {chatHistory}개 기록
                  </span>
                  <ChevronRight className="w-5 h-5 text-white/60 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </div>
          </div>

          {/* 보안 안내 */}
          <div className="max-w-7xl mx-auto px-6 pb-12">
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20 flex items-start gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">
                  엔터프라이즈급 보안
                </h4>
                <p className="text-blue-100 leading-relaxed">
                  Ash는 <strong>보안 처리 프로세스</strong>에 따라 모든 문서를{" "}
                  암호화 및 통합 저장합니다. 따라서{" "}
                  <strong>
                    문서1.pdf와 문서2.pdf를 함께 업로드한 상태에서 문서1만 개별
                    요약
                  </strong>
                  하는 것은 불가능합니다. 개별 요약을 원하시면, 해당 문서만
                  단독으로 업로드해 주세요.
                  <br /> 또한 질의응답 기능은 내부 도표에 정확한 날짜 정보가
                  포함된 경우에만 연도별 비교 분석이 가능하며, 날짜 정보가
                  누락되면 잘못된 분석이 발생할 수 있습니다.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DeepResearch;
